import React from 'react';
import dayjs from 'dayjs';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import CalculateIcon from '@mui/icons-material/Calculate';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

import Client from '../Client';
import Config from '../ClientConfig';
import { csvCell, downloadBlob } from '../ListsShared';
import {
  formatQuantityForUnit,
  isWeightUnit,
  normalizeEstimatedStockInput,
} from './orderQuantityRules';
import { configureUnicodePdf } from './pdfUnicodeFont';

const DEFAULT_LOOKBACK_DAYS = '28';
const DEFAULT_COVERAGE_DAYS = '14';
const ORDER_PROPOSAL_LEAD_DAYS = 1;
const DEFAULT_ALERT_TOLERANCE_PERCENT = 20;
const PLANNING_CELL_BACKGROUND = '#f3f8fc';
const PLANNING_HEADER_BACKGROUND = '#e8f2fa';
const QUANTITY_GUIDE = 'All quantities use the product unit. Suggested qty includes expected use until tomorrow, followed by the selected stock cover. Suggested and Order qty follow the supplier\'s order multiple—for example, 5 kg allows 5, 10, 15 kg.';

const proposalFieldSx = {
  width: '100%',
  minWidth: 0,
  '& .MuiInputBase-input': {
    fontSize: { xs: '16px', sm: '14px' },
  },
  '& .MuiInputLabel-root': { fontSize: { xs: '13px', sm: '13px' } },
};

const proposalFilterBarSx = {
  px: { xs: 1, sm: 1.5 },
  py: { xs: 0.75, sm: 1 },
  display: 'grid',
  gridTemplateColumns: { xs: '1fr 1fr', sm: '220px 260px auto' },
  gap: { xs: 1, sm: 1.25 },
  alignItems: 'end',
  borderBottom: '1px solid rgba(0,0,0,0.08)',
  flexShrink: 0,
};

const proposalAdvancedFilterBarSx = {
  ...proposalFilterBarSx,
  gridTemplateColumns: { xs: '1fr 1fr', sm: '160px 180px 180px auto' },
  backgroundColor: 'rgba(246,244,239,0.45)',
};

const proposalHeaderCellSx = (align, width) => ({
  padding: { xs: '8px 3px', sm: '7px 5px' },
  textAlign: align,
  whiteSpace: 'nowrap',
  width,
  fontWeight: 700,
  fontSize: { xs: '13px', sm: '13px', md: '14px' },
  backgroundColor: '#f6f4ef',
});

const proposalBodyCellSx = (align, width) => ({
  padding: { xs: '8px 3px', sm: '7px 5px' },
  textAlign: align,
  verticalAlign: 'top',
  whiteSpace: align === 'left' ? 'normal' : 'nowrap',
  width,
  fontSize: { xs: '13.5px', sm: '13px', md: '14px' },
  lineHeight: 1.35,
});

const proposalMainSlotSx = align => ({
  minHeight: 40,
  display: 'flex',
  alignItems: 'center',
  justifyContent: align === 'left' ? 'flex-start' : (align === 'center' ? 'center' : 'flex-end'),
});

const BLOCKING_FLAGS = new Set([
  'stock_evidence_missing',
  'stock_baseline_unreliable',
  'usage_evidence_insufficient',
  'unit_basis_unsupported',
  'invalid_multiplier',
  'estimated_stock_unavailable',
  'usage_estimate_unavailable',
]);

const VISIBLE_REVIEW_FLAGS = new Set([
  'negative_net_usage',
]);

export function createOrderProposalDraft() {
  return {
    filters: {
      location: '',
      supplier: '',
      asof: dayjs().format('YYYY-MM-DD'),
      lookbackDays: DEFAULT_LOOKBACK_DAYS,
      coverageDays: DEFAULT_COVERAGE_DAYS,
      centralSupplyOnly: false,
    },
    meta: null,
    rows: [],
    adjustments: {},
    stockAdjustments: {},
    requestFilters: null,
    status: 'idle',
    dirty: false,
    errorText: '',
  };
}

function isTrue(value) {
  return value === true || value === 1 || value === '1' || value === 'true' || value === 'True';
}

function rowKey(row, index) {
  const id = row && (row.productId ?? row.product?.iref ?? row.product?.id);
  return id == null || id === '' ? `row-${index}` : String(id);
}

function rowFlags(row) {
  return Array.isArray(row?.flags) ? row.flags.map(String) : [];
}

function hasBlockingFlag(row) {
  return rowFlags(row).some(flag => BLOCKING_FLAGS.has(flag));
}

function hasWarningFlag(row) {
  return rowFlags(row).some(flag => VISIBLE_REVIEW_FLAGS.has(flag));
}

function stockIsKnown(row) {
  return row?.estimatedStockReliable === true
    && row?.estimatedStock !== null
    && row?.estimatedStock !== undefined;
}

function stockIsReliable(row) {
  return stockIsKnown(row)
    && row?.estimatedStockReliable === true
    && row?.unitBasisSupported === true;
}

function proposalReviewSummary(rows) {
  const unknownCount = rows.filter(row => !stockIsKnown(row)).length;
  const unreliableCount = rows.filter(row => (
    stockIsKnown(row)
    && (row?.stockBaselineReliable === false || !stockIsReliable(row))
  )).length;
  const blockedCount = rows.filter(row => hasBlockingFlag(row)).length;
  const reviewCount = rows.filter(row => !hasBlockingFlag(row) && hasWarningFlag(row)).length;
  const affectedCount = rows.filter(row => (
    !stockIsKnown(row)
    || row?.stockBaselineReliable === false
    || !stockIsReliable(row)
    || hasBlockingFlag(row)
    || hasWarningFlag(row)
  )).length;
  if (!affectedCount) return null;

  const productCount = count => `${count} ${count === 1 ? 'product' : 'products'}`;
  const details = [
    unknownCount > 0 ? `${productCount(unknownCount)} ${unknownCount === 1 ? 'has' : 'have'} no safe stock estimate.` : '',
    unreliableCount > 0 ? `${productCount(unreliableCount)} ${unreliableCount === 1 ? 'has' : 'have'} a stock estimate that needs review.` : '',
    blockedCount > 0 ? `${productCount(blockedCount)} ${blockedCount === 1 ? 'has' : 'have'} no safe suggestion.` : '',
    reviewCount > 0 ? `${productCount(reviewCount)} ${reviewCount === 1 ? 'has' : 'have'} usage that needs review.` : '',
    'Review the highlighted rows. Full calculation details are available in CSV.',
  ].filter(Boolean).join(' ');

  return {
    affectedCount,
    label: `${affectedCount} ${affectedCount === 1 ? 'product needs' : 'products need'} review`,
    details,
  };
}

function proposalPlanningDetails(draft) {
  const meta = draft?.meta || {};
  const stockCoverDays = draft?.requestFilters?.coverageDays ?? '—';
  const totalTargetDays = meta.coverageDays ?? (
    Number.isFinite(Number(stockCoverDays))
      ? Number(stockCoverDays) + ORDER_PROPOSAL_LEAD_DAYS
      : '—'
  );
  return `Estimated stock through ${meta.stockCutoff || 'the prior day'}. Order date: tomorrow. Suggested qty covers ${ORDER_PROPOSAL_LEAD_DAYS} day until then plus ${stockCoverDays} days of stock cover (${totalTargetDays} days total). Usage: ${meta.historyStart || '—'}–${meta.historyEnd || '—'}. Outstanding orders are excluded.`;
}

function wholeNonNegative(value) {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : null;
}

function multiplierFor(row) {
  const multiplier = Number(row?.quantityMultiplier);
  return Number.isInteger(multiplier) && multiplier >= 1 ? multiplier : null;
}

export function adjustedStoredQuantity(row, adjustedInput) {
  const input = wholeNonNegative(adjustedInput);
  const multiplier = multiplierFor(row);
  return input === null || multiplier === null ? null : input * multiplier;
}

function inputQuantityForStored(row, storedQuantity) {
  const stored = wholeNonNegative(storedQuantity);
  const multiplier = multiplierFor(row);
  if (stored === null || multiplier === null || stored % multiplier !== 0) return null;
  return stored / multiplier;
}

function invalidOrderQuantity(row, storedQuantity) {
  if (storedQuantity === null || storedQuantity === undefined || storedQuantity === '') return false;
  return inputQuantityForStored(row, storedQuantity) === null;
}

function initialAdjustment(row) {
  const storedRecommendation = wholeNonNegative(row?.recommendedStoredQuantity);
  const recommendation = storedRecommendation === null
    ? adjustedStoredQuantity(row, row?.recommendedInputQuantity)
    : storedRecommendation;
  if (recommendation === null || hasBlockingFlag(row) || !stockIsReliable(row)) return '';
  return String(recommendation);
}

function estimatedStockFor(draft, row, index) {
  const key = rowKey(row, index);
  const adjusted = draft?.stockAdjustments?.[key];
  if (adjusted !== undefined && adjusted !== '') {
    const numeric = Number(adjusted);
    return Number.isFinite(numeric) ? numeric : null;
  }
  if (!stockIsReliable(row)) return null;
  const numeric = Number(row?.estimatedStock);
  return Number.isFinite(numeric) ? numeric : null;
}

function suggestedStoredFor(draft, row, index) {
  const estimatedStock = estimatedStockFor(draft, row, index);
  const expectedOut = row?.expectedOut === null || row?.expectedOut === undefined
    ? null
    : Number(row.expectedOut);
  const multiplier = multiplierFor(row);
  if (
    estimatedStock === null
    || expectedOut === null
    || !Number.isFinite(expectedOut)
    || multiplier === null
    || hasBlockingFlag(row)
  ) return '';
  return String(Math.ceil(Math.max(0, expectedOut - estimatedStock) / multiplier) * multiplier);
}

function alertTolerancePercent(draft) {
  const rawValue = draft?.meta?.alertTolerancePercent;
  const configured = rawValue === null || rawValue === undefined || rawValue === ''
    ? Number.NaN
    : Number(rawValue);
  return Number.isFinite(configured) && configured >= 0 && configured <= 100
    ? configured
    : DEFAULT_ALERT_TOLERANCE_PERCENT;
}

export function quantityWarningFor(draft, row, index) {
  const adjusted = wholeNonNegative(adjustmentFor(draft, row, index));
  const suggested = wholeNonNegative(suggestedStoredFor(draft, row, index));
  const estimatedStock = estimatedStockFor(draft, row, index);
  const expectedOut = row?.expectedOut === null || row?.expectedOut === undefined
    ? null
    : Number(row.expectedOut);
  const multiplier = multiplierFor(row);
  const unit = unitLabel(row?.unit);
  if (multiplier !== null && invalidOrderQuantity(row, adjustmentFor(draft, row, index))) {
    return {
      severity: 'error',
      message: `Use multiples of ${multiplier || 1}${unit ? ` ${unit}` : ''}.`,
      mobile: 'Step',
    };
  }
  if (
    adjusted === null
    || suggested === null
    || estimatedStock === null
    || expectedOut === null
    || !Number.isFinite(expectedOut)
    || multiplier === null
  ) return null;
  if (adjusted === suggested) return null;

  const rawNeed = Math.max(0, expectedOut - estimatedStock);
  const allowance = Math.max(multiplier, suggested)
    * alertTolerancePercent(draft)
    / 100;
  if (adjusted < suggested && rawNeed - adjusted > allowance + 1e-6) {
    return {
      severity: 'error',
      message: `Quantity may be too low. Suggested: ${suggested}${unit ? ` ${unit}` : ''}.`,
      mobile: 'Low',
    };
  }
  if (adjusted > suggested && adjusted - rawNeed > allowance + 1e-6) {
    return {
      severity: 'warning',
      message: `Quantity may be too high. Suggested: ${suggested}${unit ? ` ${unit}` : ''}.`,
      mobile: 'High',
    };
  }
  return null;
}

function unitLabel(unit) {
  const units = { 0: 'pc', 1: 'kg', 3: 'other', 9: 'undefined' };
  const numeric = Number(unit);
  return Object.prototype.hasOwnProperty.call(units, numeric) ? units[numeric] : String(unit ?? '');
}

function exportNumber(value, maximumFractionDigits = 6) {
  if (value === null || value === undefined || value === '') return '';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '';
  if (Number.isInteger(numeric)) return String(numeric);
  return numeric.toFixed(maximumFractionDigits).replace(/\.?0+$/, '');
}

function formatWeeklyAverage(value, fallback = '—') {
  if (value === null || value === undefined || value === '') return fallback;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return numeric.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    useGrouping: false,
  });
}

export function neutralizeCsvText(value) {
  const text = value == null ? '' : String(value);
  return /^[=+\-@]/.test(text) ? `'${text}` : text;
}

function csvText(value) {
  return csvCell(neutralizeCsvText(value));
}

function csvNumeric(value) {
  return csvCell(exportNumber(value));
}

function entityLabel(entity, fallback = '') {
  if (entity === null || entity === undefined) return fallback;
  if (typeof entity === 'string' || typeof entity === 'number') return String(entity);
  return String(entity.name || entity.label || entity.companyName || entity.legalName || entity.description || entity.iref || entity.id || fallback);
}

function entityId(entity) {
  return String(entity?.iref ?? entity?.id ?? entity?.uid ?? '');
}

function safeSelectValue(value, options) {
  const selected = String(value || '');
  return selected && options.some(option => entityId(option) === selected) ? selected : '';
}

function stripDiacritics(text = '') {
  return String(text)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ș/g, 's')
    .replace(/Ș/g, 'S')
    .replace(/ț/g, 't')
    .replace(/Ț/g, 'T');
}

function safeFilename(value, fallback) {
  const normalized = stripDiacritics(entityLabel(value, fallback))
    .replace(/[^a-z0-9_-]+/gi, '_')
    .replace(/^_+|_+$/g, '');
  return normalized || fallback;
}

function adjustmentFor(draft, row, index) {
  const key = rowKey(row, index);
  return Object.prototype.hasOwnProperty.call(draft?.adjustments || {}, key)
    ? draft.adjustments[key]
    : initialAdjustment(row);
}

export function buildPurchaseOrderDraftLines(
  draft,
  rows = visibleProposalRows(draft),
) {
  return rows.reduce((lines, row, index) => {
    const adjustedStored = adjustmentFor(draft, row, index);
    const quantity = wholeNonNegative(adjustedStored);
    if (quantity === null || quantity <= 0 || invalidOrderQuantity(row, adjustedStored)) return lines;
    const suggestedStored = suggestedStoredFor(draft, row, index);
    lines.push({
      product: row?.productName ?? row?.product?.name ?? row?.productId ?? '',
      estimatedStock: estimatedStockFor(draft, row, index),
      averageWeeklyUsage: row?.averageWeeklyUsage,
      suggestedQuantity: wholeNonNegative(suggestedStored),
      quantity,
      unit: unitLabel(row?.unit),
      unitType: row?.unit,
    });
    return lines;
  }, []);
}

function hasInvalidOrderQuantity(draft, rows) {
  return rows.some((row, index) => {
    const adjusted = wholeNonNegative(adjustmentFor(draft, row, index));
    return adjusted !== null && adjusted > 0 && invalidOrderQuantity(row, adjusted);
  });
}

export function visibleProposalRows(draft) {
  const rows = Array.isArray(draft?.rows) ? draft.rows : [];
  if (!draft?.filters?.centralSupplyOnly) return rows;
  return rows.filter(row => isTrue(row?.centralSupply));
}

export function buildOrderProposalCsv(draft, rows = visibleProposalRows(draft)) {
  const meta = draft?.meta || {};
  const generatedAt = dayjs().format('YYYY-MM-DD HH:mm:ss');
  const supplier = entityLabel(meta.supplier, draft?.requestFilters?.supplier || draft?.filters?.supplier || '');
  const location = entityLabel(meta.location, draft?.requestFilters?.location || draft?.filters?.location || '');
  const warnings = Array.isArray(meta.warnings) ? meta.warnings.map(warning => entityLabel(warning)).join(' | ') : '';

  const summary = [
    ['Order Quantity Proposal - Not Placed', ''],
    ['Supplier', supplier],
    ['Supplier ID', meta?.supplier?.iref ?? draft?.requestFilters?.supplier ?? draft?.filters?.supplier ?? ''],
    ['Location', location],
    ['Location ID', meta?.location?.iref ?? draft?.requestFilters?.location ?? draft?.filters?.location ?? ''],
    ['As-of date', meta.asof || draft?.requestFilters?.asof || ''],
    ['Stock cutoff', meta.stockCutoff || ''],
    ['History start', meta.historyStart || ''],
    ['History end', meta.historyEnd || ''],
    ['Lookback days', meta.lookbackDays ?? draft?.requestFilters?.lookbackDays ?? ''],
    ['Order lead days', ORDER_PROPOSAL_LEAD_DAYS],
    ['Stock cover after order date', draft?.requestFilters?.coverageDays ?? ''],
    ['Total target days from as-of', meta.coverageDays ?? ''],
    ['Coverage starts on', meta.coverageStartsOn || ''],
    ['Formula version', meta.formulaVersion || ''],
    ['Usage method', meta.usageMethod || ''],
    ['Quantity basis', meta.quantityBasis || ''],
    ['Usage evidence sufficient', meta.usageEvidenceSufficient === true ? 'Yes' : 'No'],
    ['Sales completeness verified', meta.salesCompletenessVerified === true ? 'Yes' : 'No'],
    ['Observed sales activity coverage', exportNumber(meta.salesActivityCoverageRate, 4)],
    ['Expected POS days', exportNumber(meta.expectedPosDays, 0)],
    ['Observed POS days', exportNumber(meta.observedPosDays, 0)],
    ['Missing recipe product count', exportNumber(meta.missingRecipeProductCount, 0)],
    ['Open orders included', meta.openOrdersIncluded === true ? 'Yes' : 'No'],
    ['Existing order flow supported', meta.existingOrderFlowSupported === true ? 'Yes' : 'No'],
    ['Warnings', warnings],
    ['Generated at', generatedAt],
    ['', ''],
  ].map(([label, value]) => [csvText(label), csvText(value)].join(','));

  const header = [
    'Product ID',
    'Product',
    'Unit',
    'Preferred supplier relation',
    'Central supply',
    'Stock computed',
    'Stock baseline reliable',
    'Unit basis supported',
    'Stock baseline date',
    'Stock status',
    'Ledger stock',
    'Estimated stock',
    'Adjusted estimated stock',
    'Estimated stock reliable',
    'Estimated consumption since close',
    'Confirmed non-demand net since close',
    'Usage in window',
    'Usage source',
    'Usage confidence',
    'Average/day',
    'Average/week',
    'Days of stock',
    'Target stored quantity',
    'Raw shortfall',
    'Adjusted raw shortfall',
    'Min reference',
    'Reorder reference',
    'Multiplier',
    'Multiplier valid',
    'Suggested input quantity',
    'Suggested stored quantity',
    'Adjusted input quantity',
    'Adjusted stored quantity',
    'Order-form compatible',
    'Flags',
  ].map(csvText).join(',');

  const detail = rows.map((row, index) => {
    const known = stockIsKnown(row);
    const adjustedStored = wholeNonNegative(adjustmentFor(draft, row, index));
    const adjustedInput = inputQuantityForStored(row, adjustedStored);
    const adjustedStock = estimatedStockFor(draft, row, index);
    const adjustedSuggestionStored = suggestedStoredFor(draft, row, index);
    const adjustedSuggestionInput = inputQuantityForStored(row, adjustedSuggestionStored);
    const expectedOut = row?.expectedOut === null || row?.expectedOut === undefined
      ? null
      : Number(row.expectedOut);
    const adjustedRawShortfall = (
      adjustedStock !== null
      && expectedOut !== null
      && Number.isFinite(expectedOut)
    ) ? Math.max(0, expectedOut - adjustedStock) : null;
    return [
      csvText(row?.productId ?? row?.product?.iref ?? ''),
      csvText(row?.productName ?? row?.product?.name ?? ''),
      csvText(unitLabel(row?.unit)),
      csvText(isTrue(row?.preferred) ? 'Yes' : 'No'),
      csvText(isTrue(row?.centralSupply) ? 'Yes' : 'No'),
      csvText(row?.stockComputed === true ? 'Yes' : 'No'),
      csvText(row?.stockBaselineReliable === true ? 'Yes' : 'No'),
      csvText(row?.unitBasisSupported === true ? 'Yes' : 'No'),
      csvText(row?.stockBaselineDate || ''),
      csvText(!known ? 'Unknown' : (stockIsReliable(row) ? 'Known' : 'Unreliable')),
      csvNumeric(row?.currentStock),
      csvNumeric(row?.estimatedStock),
      csvNumeric(adjustedStock),
      csvText(row?.estimatedStockReliable === true ? 'Yes' : 'No'),
      csvNumeric(row?.estimatedConsumptionSinceClose),
      csvNumeric(row?.confirmedNonDemandNetSinceClose),
      csvNumeric(row?.usageInWindow),
      csvText(row?.usageSource),
      csvText(row?.usageConfidence),
      csvNumeric(row?.averageDailyUsage),
      csvNumeric(row?.averageWeeklyUsage),
      csvNumeric(row?.daysOfStock),
      csvNumeric(row?.targetStoredQuantity),
      csvNumeric(row?.rawShortfall),
      csvNumeric(adjustedRawShortfall),
      csvNumeric(row?.minQuantity),
      csvNumeric(row?.reorderQuantity),
      csvNumeric(row?.quantityMultiplier),
      csvText(row?.multiplierValid === true ? 'Yes' : 'No'),
      csvNumeric(adjustedSuggestionInput),
      csvNumeric(adjustedSuggestionStored),
      csvNumeric(adjustedInput),
      csvNumeric(adjustedStored),
      csvText(isTrue(row?.orderFormCompatible) ? 'Yes' : 'No'),
      csvText(rowFlags(row).join(' | ')),
    ].join(',');
  });

  return [...summary, header, ...detail].join('\r\n');
}

function lookupArray(response, keys) {
  if (Array.isArray(response)) return response;
  for (const key of keys) {
    if (Array.isArray(response?.[key])) return response[key];
  }
  return [];
}

function requestError(error, fallback) {
  const body = error?.responseJSON || error || {};
  if (typeof body === 'string') return body;
  if (body instanceof Error && body.message) return body.message;
  if (
    typeof body?.getResponseHeader === 'function'
    || typeof body?.getAllResponseHeaders === 'function'
    || typeof body?.abort === 'function'
  ) {
    return String(fallback);
  }
  const direct = body.detail || body.error || body.message;
  if (Array.isArray(direct)) return direct.join(' ');
  if (
    typeof direct === 'string'
    || typeof direct === 'number'
    || typeof direct === 'boolean'
  ) {
    return String(direct);
  }
  if (body && typeof body === 'object') {
    const fieldMessages = Object.entries(body).flatMap(([field, messages]) => {
      const values = Array.isArray(messages) ? messages : [messages];
      return values
        .filter(message => (
          typeof message === 'string'
          || typeof message === 'number'
          || typeof message === 'boolean'
        ))
        .filter(message => String(message).trim() !== '')
        .map(message => `${field}: ${String(message)}`);
    });
    if (fieldMessages.length > 0) return fieldMessages.join(' ');
  }
  return String(fallback);
}

class PurchaseOrderRecommendationUI extends React.Component {
  constructor(props) {
    super(props);
    this.client = props.client || new Client('none');
    this.state = {
      locations: [],
      suppliers: [],
      internalSupplier: '',
      lookupsLoading: true,
      lookupError: '',
      advancedSettingsOpen: false,
    };
    this._mounted = false;
    this._lookupRequests = [];
    this._lookupSequence = 0;
    this._proposalRequest = null;
    this._requestSequence = 0;
  }

  componentDidMount() {
    this._mounted = true;
    this.loadLookups();
  }

  componentWillUnmount() {
    this._mounted = false;
    this._lookupSequence += 1;
    this._lookupRequests.forEach(request => {
      if (request && typeof request.abort === 'function') request.abort();
    });
    if (this._proposalRequest && typeof this._proposalRequest.abort === 'function') {
      this._proposalRequest.abort();
    }
  }

  getDraft = () => this.props.draft || createOrderProposalDraft();

  setDraft = (updater) => {
    const current = this.getDraft();
    const next = typeof updater === 'function' ? updater(current) : updater;
    if (this.props.onDraftChange) this.props.onDraftChange(next);
  };

  loadLookups = () => {
    const sequence = ++this._lookupSequence;
    this.setState({ lookupsLoading: true, lookupError: '' });
    try {
      const filtersRequest = this.client.getDataAsync(
        Config.API_URL_PURCHASE_ORDER_RECOMMENDATION_FILTERS,
        {},
      );
      this._lookupRequests = [filtersRequest];

      Promise.resolve(filtersRequest).then(response => {
        if (!this._mounted || sequence !== this._lookupSequence) return;
        const locations = lookupArray(response, ['locations', 'results'])
          .slice()
          .sort((a, b) => entityLabel(a).localeCompare(entityLabel(b)));
        const suppliers = lookupArray(response, ['suppliers'])
          .slice()
          .sort((a, b) => entityLabel(a).localeCompare(entityLabel(b)));
        const internalSupplier = String(response?.internalSupplier ?? '');
        const issueText = Array.isArray(response?.issues)
          ? response.issues.map(issue => entityLabel(issue?.message || issue)).filter(Boolean).join(' ')
          : '';
        this.setDraft(previous => {
          const previousFilters = previous.filters || {};
          const location = safeSelectValue(previousFilters.location, locations);
          const supplier = safeSelectValue(previousFilters.supplier, suppliers);
          if (location === String(previousFilters.location || '') && supplier === String(previousFilters.supplier || '')) {
            return previous;
          }
          return {
            ...previous,
            filters: {
              ...previousFilters,
              location,
              supplier,
              centralSupplyOnly: supplier ? previousFilters.centralSupplyOnly : false,
            },
            dirty: previous.requestFilters ? true : previous.dirty,
            errorText: '',
          };
        });
        this.setState({
          locations,
          suppliers,
          internalSupplier,
          lookupsLoading: false,
          lookupError: issueText || (locations.length && suppliers.length ? '' : 'Some proposal filters could not be loaded.'),
        });
      }).catch(error => {
        if (!this._mounted || sequence !== this._lookupSequence) return;
        if (error?.statusText === 'abort') return;
        this.setState({
          lookupsLoading: false,
          lookupError: requestError(error, 'Failed to load proposal filters.'),
        });
      });
    } catch (error) {
      this.setState({
        lookupsLoading: false,
        lookupError: requestError(error, 'Failed to load proposal filters.'),
      });
    }
  };

  updateFilter = (field, value, affectsRequest = true) => {
    this.setDraft(previous => ({
      ...previous,
      filters: {
        ...(previous.filters || {}),
        [field]: value,
      },
      dirty: affectsRequest && previous.requestFilters ? true : previous.dirty,
      errorText: '',
    }));
  };

  onSupplierChange = (value) => {
    const supplier = String(value || '');
    this.setDraft(previous => ({
      ...previous,
      filters: {
        ...(previous.filters || {}),
        supplier,
        centralSupplyOnly: !!supplier && supplier === String(this.state.internalSupplier || ''),
      },
      dirty: previous.requestFilters ? true : previous.dirty,
      errorText: '',
    }));
  };

  validateFilters = () => {
    const filters = this.getDraft().filters || {};
    if (!filters.location) return 'Select a location.';
    if (!filters.supplier) return 'Select a supplier.';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(filters.asof || '') || !dayjs(filters.asof).isValid()) {
      return 'Select a valid as-of date.';
    }
    if (dayjs(filters.asof).isAfter(dayjs(), 'day')) return 'As-of date cannot be in the future.';
    const lookbackDays = Number(filters.lookbackDays);
    const coverageDays = Number(filters.coverageDays);
    if (!Number.isInteger(lookbackDays) || lookbackDays < 7 || lookbackDays > 84) {
      return 'History days must be a whole number from 7 to 84.';
    }
    if (!Number.isInteger(coverageDays) || coverageDays < 1 || coverageDays > 60 - ORDER_PROPOSAL_LEAD_DAYS) {
      return 'Stock cover must be a whole number from 1 to 59.';
    }
    return '';
  };

  calculateProposal = () => {
    const validationError = this.validateFilters();
    if (validationError) {
      this.setDraft(previous => ({ ...previous, status: 'error', errorText: validationError }));
      return;
    }

    if (this._proposalRequest && typeof this._proposalRequest.abort === 'function') {
      this._proposalRequest.abort();
    }

    const filters = this.getDraft().filters || {};
    const requestFilters = {
      location: String(filters.location),
      supplier: String(filters.supplier),
      asof: filters.asof,
      lookbackDays: Number(filters.lookbackDays),
      coverageDays: Number(filters.coverageDays),
    };
    const recommendationFilters = {
      ...requestFilters,
      coverageDays: requestFilters.coverageDays + ORDER_PROPOSAL_LEAD_DAYS,
    };
    const sequence = ++this._requestSequence;

    this.setDraft(previous => ({
      ...previous,
      status: 'loading',
      errorText: '',
    }));

    try {
      this._proposalRequest = this.client.getDataAsync(
        Config.API_URL_PURCHASE_ORDER_RECOMMENDATIONS,
        recommendationFilters,
      );
      Promise.resolve(this._proposalRequest).then(response => {
        if (!this._mounted || sequence !== this._requestSequence) return;
        if (response?.error) throw response;
        if (!response || !Array.isArray(response.items)) {
          throw new Error('The recommendation service returned an invalid response.');
        }
        const rows = response.items.slice();
        const adjustments = {};
        const stockAdjustments = {};
        rows.forEach((row, index) => {
          adjustments[rowKey(row, index)] = initialAdjustment(row);
          stockAdjustments[rowKey(row, index)] = '';
        });
        this.setDraft(previous => ({
          ...previous,
          meta: response?.meta || {},
          rows,
          adjustments,
          stockAdjustments,
          requestFilters,
          status: 'success',
          dirty: false,
          errorText: '',
        }));
      }).catch(error => {
        if (!this._mounted || sequence !== this._requestSequence) return;
        if (error?.statusText === 'abort') return;
        this.setDraft(previous => ({
          ...previous,
          status: 'error',
          dirty: true,
          errorText: requestError(error, 'Failed to calculate the proposal.'),
        }));
      });
    } catch (error) {
      this.setDraft(previous => ({
        ...previous,
        status: 'error',
        dirty: true,
        errorText: requestError(error, 'Failed to calculate the proposal.'),
      }));
    }
  };

  updateAdjustment = (row, index, value) => {
    if (!/^\d*$/.test(value)) return;
    const key = rowKey(row, index);
    this.setDraft(previous => ({
      ...previous,
      adjustments: {
        ...(previous.adjustments || {}),
        [key]: value,
      },
    }));
  };

  updateStockAdjustment = (row, index, value) => {
    const normalized = normalizeEstimatedStockInput(value, row?.unit);
    if (normalized === null) return;
    const key = rowKey(row, index);
    this.setDraft(previous => ({
      ...previous,
      stockAdjustments: {
        ...(previous.stockAdjustments || {}),
        [key]: normalized,
      },
    }));
  };

  resetAdjustments = () => {
    this.setDraft(previous => {
      const adjustments = {};
      const stockAdjustments = {};
      (previous.rows || []).forEach((row, index) => {
        adjustments[rowKey(row, index)] = initialAdjustment(row);
        stockAdjustments[rowKey(row, index)] = '';
      });
      return { ...previous, adjustments, stockAdjustments };
    });
  };

  clearAdjustments = () => {
    this.setDraft(previous => {
      const adjustments = {};
      (previous.rows || []).forEach((row, index) => {
        adjustments[rowKey(row, index)] = '';
      });
      return { ...previous, adjustments };
    });
  };

  exportCsv = () => {
    const draft = this.getDraft();
    const rows = visibleProposalRows(draft);
    if (!rows.length || draft.dirty) return;
    const supplier = draft.meta?.supplier || draft.requestFilters?.supplier;
    const date = draft.meta?.asof || draft.requestFilters?.asof || dayjs().format('YYYY-MM-DD');
    downloadBlob(
      buildOrderProposalCsv(draft, rows),
      `order_proposal_${safeFilename(supplier, 'supplier')}_${date}.csv`,
      'text/csv;charset=utf-8;',
    );
  };

  exportPdf = async () => {
    const draft = this.getDraft();
    const rows = visibleProposalRows(draft);
    const draftLines = buildPurchaseOrderDraftLines(draft, rows);
    if (!draftLines.length || draft.dirty || hasInvalidOrderQuantity(draft, rows)) return;
    const meta = draft.meta || {};
    const supplier = entityLabel(meta.supplier, draft.requestFilters?.supplier || '');
    const location = entityLabel(meta.location, draft.requestFilters?.location || '');
    const generatedAt = dayjs().format('YYYY-MM-DD HH:mm:ss');
    const proposalDate = meta.asof || draft.requestFilters?.asof || draft.filters?.asof || '';
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    const margin = 34;
    let pdfFont;
    try {
      pdfFont = await configureUnicodePdf(doc);
    } catch (error) {
      console.error('Could not load the Unicode PDF font.', error);
      window.alert('The PDF font could not be loaded. Please try again.');
      return;
    }

    doc.setFontSize(15);
    doc.text('Purchase Order Draft - Not Placed', margin, 34);
    doc.setFontSize(9);
    const metadata = [
      `Supplier: ${supplier}`,
      `Location: ${location}`,
      `Prepared: ${generatedAt}`,
      `Stock date: ${proposalDate}`,
    ];
    let metadataY = 51;
    metadata.forEach(line => {
      doc.text(line, margin, metadataY);
      metadataY += 12;
    });

    autoTable(doc, {
      startY: metadataY + 10,
      head: [['Product', 'Est. stock', 'Avg/week', 'Suggested', 'Order qty', 'Unit']],
      body: draftLines.map(line => [
        line.product,
        formatQuantityForUnit(line.estimatedStock, line.unitType, '—'),
        formatWeeklyAverage(line.averageWeeklyUsage),
        formatQuantityForUnit(line.suggestedQuantity, line.unitType, '—'),
        formatQuantityForUnit(line.quantity, line.unitType, '—'),
        line.unit,
      ]),
      margin: { left: margin, right: margin, bottom: 34 },
      theme: 'grid',
      styles: { font: pdfFont, fontStyle: 'normal', fontSize: 8, cellPadding: 4, overflow: 'linebreak' },
      headStyles: { fillColor: [39, 52, 66], textColor: 255, fontStyle: 'normal' },
      columnStyles: {
        1: { halign: 'right', cellWidth: 76 },
        2: { halign: 'right', cellWidth: 76 },
        3: { halign: 'right', cellWidth: 76 },
        4: { halign: 'right', cellWidth: 76 },
        5: { cellWidth: 45 },
      },
    });

    const pageCount = doc.getNumberOfPages();
    for (let page = 1; page <= pageCount; page += 1) {
      doc.setPage(page);
      doc.setFont(pdfFont, 'normal');
      doc.setFontSize(8);
      doc.text(
        'Draft only - verify quantities before ordering.',
        margin,
        doc.internal.pageSize.getHeight() - 16,
      );
      doc.text(
        `Page ${page} of ${pageCount}`,
        doc.internal.pageSize.getWidth() - margin,
        doc.internal.pageSize.getHeight() - 16,
        { align: 'right' },
      );
    }
    doc.save(`order_proposal_${safeFilename(meta.supplier || supplier, 'supplier')}_${meta.asof || 'date'}.pdf`);
  };

  renderFilters() {
    const draft = this.getDraft();
    const filters = draft.filters || {};
    const disabled = draft.status === 'loading';
    const locationValue = safeSelectValue(filters.location, this.state.locations);
    const supplierValue = safeSelectValue(filters.supplier, this.state.suppliers);
    return (
      <Box sx={{ flexShrink: 0 }}>
        <Box sx={proposalFilterBarSx}>
          <TextField
            select
            required
            variant="standard"
            size="small"
            label="Location"
            value={locationValue}
            onChange={event => this.updateFilter('location', event.target.value)}
            disabled={disabled || this.state.lookupsLoading}
            sx={proposalFieldSx}
          >
            <MenuItem value="">Select location</MenuItem>
            {this.state.locations.map(location => {
              const id = entityId(location);
              return <MenuItem key={id} value={id}>{entityLabel(location, id)}</MenuItem>;
            })}
          </TextField>

          <TextField
            select
            required
            variant="standard"
            size="small"
            label="Supplier"
            value={supplierValue}
            onChange={event => this.onSupplierChange(event.target.value)}
            disabled={disabled || this.state.lookupsLoading}
            sx={proposalFieldSx}
          >
            <MenuItem value="">Select supplier</MenuItem>
            {this.state.suppliers.map(supplier => {
              const id = entityId(supplier);
              return <MenuItem key={id} value={id}>{entityLabel(supplier, id)}</MenuItem>;
            })}
          </TextField>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'space-between', sm: 'flex-start' }, gap: 0.75, gridColumn: { xs: '1 / -1', sm: 'auto' } }}>
            <Button
              size="small"
              variant="text"
              onClick={() => this.setState(previous => ({ advancedSettingsOpen: !previous.advancedSettingsOpen }))}
              aria-expanded={this.state.advancedSettingsOpen}
            >
              {this.state.advancedSettingsOpen ? 'Hide settings' : 'More settings'}
            </Button>
            {!!filters.centralSupplyOnly && !this.state.advancedSettingsOpen && (
              <Chip size="small" variant="outlined" label="Central only" />
            )}
            <Button
              variant="contained"
              size="small"
              startIcon={disabled ? <CircularProgress size={16} color="inherit" /> : <CalculateIcon />}
              onClick={this.calculateProposal}
              disabled={disabled || this.state.lookupsLoading}
            >
              {disabled ? 'Calculating…' : 'Calculate'}
            </Button>
          </Box>
        </Box>

        {this.state.advancedSettingsOpen && (
          <Box sx={proposalAdvancedFilterBarSx}>
            <TextField
              required
              variant="standard"
              size="small"
              label="Stock date"
              type="date"
              value={filters.asof || ''}
              onChange={event => this.updateFilter('asof', event.target.value)}
              disabled={disabled}
              InputLabelProps={{ shrink: true }}
              sx={proposalFieldSx}
            />

            <TextField
              required
              variant="standard"
              size="small"
              label="History (days)"
              type="number"
              value={filters.lookbackDays ?? ''}
              onChange={event => this.updateFilter('lookbackDays', event.target.value)}
              disabled={disabled}
              inputProps={{ min: 7, max: 84, step: 1 }}
              sx={proposalFieldSx}
            />

            <Tooltip title="Desired stock cover after tomorrow's order date.">
              <TextField
                required
                variant="standard"
                size="small"
                label="Stock cover (days)"
                type="number"
                value={filters.coverageDays ?? ''}
                onChange={event => this.updateFilter('coverageDays', event.target.value)}
                disabled={disabled}
                inputProps={{ min: 1, max: 59, step: 1 }}
                sx={proposalFieldSx}
              />
            </Tooltip>

            <FormControlLabel
              control={(
                <Checkbox
                  checked={!!filters.centralSupplyOnly}
                  onChange={event => this.updateFilter('centralSupplyOnly', event.target.checked, false)}
                  disabled={disabled}
                />
              )}
              label="Central supply only"
            />
          </Box>
        )}

        {!this.state.lookupsLoading && !!this.state.lookupError && (
          <Typography variant="caption" color="warning.dark" sx={{ display: 'block', px: 1, py: 0.4 }}>
            {this.state.lookupError}
          </Typography>
        )}
        {!this.state.lookupsLoading && !!draft.errorText && (
          <Typography variant="caption" color="error" sx={{ display: 'block', px: 1, py: 0.4 }}>
            {draft.errorText}
          </Typography>
        )}
        {!this.state.lookupsLoading && draft.dirty && draft.requestFilters && (
          <Typography variant="caption" color="warning.dark" sx={{ display: 'block', px: 1, py: 0.4 }}>
            Filters changed. Recalculate before exporting or using these results.
          </Typography>
        )}
      </Box>
    );
  }

  renderTable(rows) {
    const draft = this.getDraft();
    const canExportCsv = rows.length > 0 && !draft.dirty && draft.status !== 'loading';
    const canExportPdf = canExportCsv
      && !hasInvalidOrderQuantity(draft, rows)
      && buildPurchaseOrderDraftLines(draft, rows).length > 0;
    const reviewSummary = proposalReviewSummary(rows);
    const planningDetails = !this.state.lookupsLoading && draft.meta && draft.status !== 'loading' && !draft.dirty
      ? proposalPlanningDetails(draft)
      : '';
    return (
      <Box sx={{ position: 'relative', flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <Stack
          direction="row"
          alignItems="center"
          sx={{
            flexWrap: { xs: 'nowrap', sm: 'wrap' },
            gap: { xs: 0.25, sm: 0.5 },
            px: 0.75,
            py: 0.5,
            borderBottom: '1px solid rgba(0,0,0,0.08)',
          }}
        >
          <Typography variant="subtitle2" sx={{ display: { xs: 'none', sm: 'block' } }}>{rows.length} {rows.length === 1 ? 'product' : 'products'}</Typography>
          <Tooltip title={QUANTITY_GUIDE} arrow describeChild enterTouchDelay={0} leaveTouchDelay={5000}>
            <Button
              size="small"
              variant="text"
              aria-label="Quantity guide"
              sx={{ minWidth: { xs: 32, sm: 0 }, px: 0.5, gap: 0.4, textTransform: 'none' }}
            >
              <InfoOutlinedIcon fontSize="small" />
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Quantities</Box>
            </Button>
          </Tooltip>
          {!!planningDetails && (
            <Tooltip title={planningDetails} arrow describeChild enterTouchDelay={0} leaveTouchDelay={5000}>
              <Button
                size="small"
                variant="text"
                aria-label="Planning details"
                sx={{ minWidth: { xs: 32, sm: 0 }, px: 0.5, gap: 0.4, textTransform: 'none' }}
              >
                <CalendarMonthOutlinedIcon fontSize="small" />
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Plan details</Box>
              </Button>
            </Tooltip>
          )}
          {reviewSummary && (
            <Tooltip title={reviewSummary.details} arrow describeChild enterTouchDelay={0} leaveTouchDelay={5000}>
              <Chip
                size="small"
                color="warning"
                variant="outlined"
                icon={<WarningAmberOutlinedIcon sx={{ display: { xs: 'none', sm: 'inherit' } }} />}
                label={(
                  <>
                    <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>{reviewSummary.affectedCount} review</Box>
                    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{reviewSummary.label}</Box>
                  </>
                )}
                tabIndex={0}
                aria-label={`${reviewSummary.label}. ${reviewSummary.details}`}
                sx={{ '& .MuiChip-label': { px: { xs: 0.75, sm: 1.5 } } }}
              />
            </Tooltip>
          )}
          <Tooltip title="Adjustments stay in this page. CSV/PDF export does not place an order." arrow>
            <Chip size="small" variant="outlined" label="Draft only" sx={{ display: { xs: 'none', sm: 'inline-flex' } }} />
          </Tooltip>
          <Button size="small" variant="text" startIcon={<RestartAltIcon />} onClick={this.resetAdjustments} disabled={!draft.rows.length} sx={{ minWidth: { xs: 0, sm: 64 }, px: { xs: 0.5, sm: 1 }, '& .MuiButton-startIcon': { display: { xs: 'none', sm: 'inherit' } } }}>
            Reset
          </Button>
          <Button size="small" variant="text" startIcon={<DeleteSweepIcon />} onClick={this.clearAdjustments} disabled={!draft.rows.length} sx={{ minWidth: { xs: 0, sm: 64 }, px: { xs: 0.5, sm: 1 }, '& .MuiButton-startIcon': { display: { xs: 'none', sm: 'inherit' } } }}>
            Clear
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button size="small" variant="outlined" startIcon={<FileDownloadIcon />} onClick={this.exportCsv} disabled={!canExportCsv} sx={{ display: { xs: 'none', sm: 'inline-flex' } }}>
            CSV
          </Button>
          <Button size="small" variant="outlined" startIcon={<FileDownloadIcon />} onClick={this.exportPdf} disabled={!canExportPdf} sx={{ minWidth: { xs: 0, sm: 64 }, px: { xs: 0.75, sm: 1.25 }, '& .MuiButton-startIcon': { display: { xs: 'none', sm: 'inherit' } } }}>
            PDF
          </Button>
        </Stack>

        <TableContainer sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <Table
            stickyHeader
            size="small"
            sx={{
              minWidth: { xs: 0, sm: 780 },
              width: '100%',
              tableLayout: 'fixed',
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...proposalHeaderCellSx('left'), width: { xs: '35%', sm: '27%' } }}><b>Product</b></TableCell>
                <TableCell sx={{ ...proposalHeaderCellSx('right'), width: { xs: '21%', sm: '13%' }, bgcolor: PLANNING_HEADER_BACKGROUND }}>
                  <Tooltip title="Editable estimate through the prior day" arrow><Box component="span">Est. stock</Box></Tooltip>
                </TableCell>
                <TableCell sx={{ ...proposalHeaderCellSx('right', '15%'), display: { xs: 'none', sm: 'table-cell' } }}>
                  <Tooltip title="Theoretical average weekly sale/consumption" arrow><Box component="span">Avg/week</Box></Tooltip>
                </TableCell>
                <TableCell sx={{ ...proposalHeaderCellSx('right'), width: { xs: '20%', sm: '13%' }, bgcolor: PLANNING_HEADER_BACKGROUND }}>
                  <Tooltip title="System-recommended order quantity" arrow><Box component="span">Suggested<Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}> qty</Box></Box></Tooltip>
                </TableCell>
                <TableCell sx={{ ...proposalHeaderCellSx('right'), width: { xs: '24%', sm: '15%' } }}>
                  <Tooltip title="Editable proposal quantity" arrow><Box component="span">Order qty</Box></Tooltip>
                </TableCell>
                <TableCell sx={{ ...proposalHeaderCellSx('left', '17%'), display: { xs: 'none', sm: 'table-cell' } }}><b>Status</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => {
                const known = stockIsKnown(row);
                const reliable = stockIsReliable(row);
                const adjustedStored = adjustmentFor(draft, row, index);
                const suggestedStored = suggestedStoredFor(draft, row, index);
                const adjustedStock = draft?.stockAdjustments?.[rowKey(row, index)] ?? '';
                const displayedStock = adjustedStock !== ''
                  ? adjustedStock
                  : (known ? formatQuantityForUnit(row?.estimatedStock, row?.unit) : '');
                const quantityWarning = quantityWarningFor(draft, row, index);
                const key = rowKey(row, index);
                const multiplier = multiplierFor(row);
                const planningCellBackground = quantityWarning || !reliable || hasBlockingFlag(row)
                  ? 'inherit'
                  : PLANNING_CELL_BACKGROUND;
                let status = { label: 'Ready', color: 'success', detail: 'The suggestion is available.' };
                if (!known) {
                  status = { label: 'No estimate', color: 'warning', detail: 'Estimated stock could not be calculated safely, so no suggestion was prefilled.' };
                } else if (row?.stockBaselineReliable === false) {
                  status = { label: 'Check estimate', color: 'warning', detail: 'Estimated stock needs review, so no suggestion was prefilled.' };
                } else if (hasBlockingFlag(row) || !reliable) {
                  status = { label: 'Needs review', color: 'warning', detail: 'The available evidence does not support a safe suggestion.' };
                } else if (hasWarningFlag(row)) {
                  status = { label: 'Check usage', color: 'warning', detail: 'Recorded returns exceed usage in the selected history. Check the suggestion.' };
                }
                return (
                  <TableRow key={key} hover sx={{ bgcolor: quantityWarning?.severity === 'error' ? '#ffebee' : (!reliable || hasBlockingFlag(row) || quantityWarning ? '#fff8e1' : undefined) }}>
                    <TableCell sx={{ ...proposalBodyCellSx('left'), width: { xs: '35%', sm: '27%' }, wordBreak: 'break-word' }}>
                      <Box sx={proposalMainSlotSx('left')}>
                        <Tooltip title={String(row?.productId ?? '')} arrow>
                          <Typography component="span" variant="body2">{row?.productName ?? row?.product?.name ?? row?.productId ?? '—'}</Typography>
                        </Tooltip>
                      </Box>
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                        {unitLabel(row?.unit)}
                        {multiplier > 1 && (
                          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}> · Order multiple: {multiplier} {unitLabel(row?.unit)}</Box>
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ ...proposalBodyCellSx('right'), width: { xs: '21%', sm: '13%' }, bgcolor: planningCellBackground }}>
                      <Box sx={proposalMainSlotSx('right')}>
                        <TextField
                          size="small"
                          value={displayedStock}
                          placeholder="—"
                          onChange={event => this.updateStockAdjustment(row, index, event.target.value)}
                          inputProps={{
                            step: isWeightUnit(row?.unit) ? '0.001' : '1',
                            inputMode: isWeightUnit(row?.unit) ? 'decimal' : 'numeric',
                            pattern: isWeightUnit(row?.unit)
                              ? '^-?[0-9]+([.,][0-9]{0,3})?$'
                              : '^-?[0-9]+$',
                            style: { textAlign: 'right' },
                            'aria-label': `Estimated stock for ${row?.productName ?? key}`,
                          }}
                          sx={{ width: { xs: '100%', sm: 82 }, maxWidth: '100%', minWidth: 0 }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell sx={{ ...proposalBodyCellSx('right', '15%'), display: { xs: 'none', sm: 'table-cell' } }}>
                      <Box sx={proposalMainSlotSx('right')}>{formatWeeklyAverage(row?.averageWeeklyUsage)}</Box>
                    </TableCell>
                    <TableCell sx={{ ...proposalBodyCellSx('right'), width: { xs: '20%', sm: '13%' }, fontWeight: 600, bgcolor: planningCellBackground }}>
                      <Box sx={proposalMainSlotSx('right')}>{suggestedStored === '' ? '—' : formatQuantityForUnit(suggestedStored, row?.unit, '—')}</Box>
                    </TableCell>
                    <TableCell sx={{ ...proposalBodyCellSx('right'), width: { xs: '24%', sm: '15%' } }}>
                      <Box sx={proposalMainSlotSx('right')}>
                        <TextField
                          size="small"
                          value={adjustedStored}
                          error={quantityWarning?.severity === 'error'}
                          onChange={event => this.updateAdjustment(row, index, event.target.value)}
                          inputProps={{
                            min: 0,
                            step: multiplier || 1,
                            inputMode: 'numeric',
                            pattern: '[0-9]*',
                            'aria-label': `Order quantity for ${row?.productName ?? row?.productId ?? key}`,
                            style: { textAlign: 'right' },
                          }}
                          sx={{ width: { xs: '100%', sm: 82 }, maxWidth: '100%', minWidth: 0 }}
                        />
                      </Box>
                      {quantityWarning && (
                        <Typography variant="caption" color={quantityWarning.severity === 'error' ? 'error' : 'warning.dark'} sx={{ display: 'block', mt: 0.25, lineHeight: 1.2 }}>
                          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{quantityWarning.message}</Box>
                          <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>{quantityWarning.mobile}</Box>
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ ...proposalBodyCellSx('left', '17%'), display: { xs: 'none', sm: 'table-cell' } }}>
                      <Box sx={proposalMainSlotSx('left')}>
                        <Tooltip title={status.detail} arrow>
                          <Chip size="small" color={status.color} variant={status.color === 'success' ? 'outlined' : 'filled'} label={status.label} />
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!rows.length && draft.status !== 'loading' && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    {draft.dirty
                      ? 'Filters changed. Calculate the proposal again.'
                      : (draft.status === 'success' ? 'No supplier products match the selected view.' : 'Select filters and calculate a proposal.')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {draft.status === 'loading' && (
          <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(255,255,255,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        )}
      </Box>
    );
  }

  render() {
    const draft = this.getDraft();
    const rows = visibleProposalRows(draft);
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          maxHeight: '100%',
          minHeight: 0,
          minWidth: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {this.renderFilters()}
        {this.renderTable(rows)}
      </Box>
    );
  }
}

export default PurchaseOrderRecommendationUI;
